import { Button, Space, Tooltip, Typography } from 'antd';
import React, { useState } from 'react';
import { ArrowLeftOutlined, MessageOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import ProviderGrid from './components/ProviderGrid';
import { ResourceItem, ResourceSelector } from './components/ResourceSelector';
import { PARTNER_OPTIONS } from './constants';
import { PlaygroundContext } from './context/PlaygroundContext';
import { GET_MODELS } from './graphql/queries';
import { getModelInferenceTypes } from './utils/model-validation';
import { getProviderLogo } from './utils/providerLogo';

const { Title } = Typography;

const DirectModelInteractionContent: React.FC = () => {
  const BackIcon = ArrowLeftOutlined as unknown as React.FC<any>;
  const MessageIcon = MessageOutlined as unknown as React.FC<any>;
  const { setCurrentView } = React.useContext(PlaygroundContext);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [selectedInferenceTypes, setSelectedInferenceTypes] = useState<
    string[]
  >([]);

  const { data: modelsData, loading: modelsLoading } = useQuery<{
    modelsList: ResourceItem[];
  }>(GET_MODELS);

  const models: ResourceItem[] = modelsData?.modelsList || [];

  // Filter: Only models that support TEXT in both input and output modalities
  const extractModalities = React.useCallback((mods: unknown): string[] => {
    if (!Array.isArray(mods)) return [];
    return mods
      .map((v) => {
        if (typeof v === 'string') return v;
        if (v && typeof v === 'object') {
          const obj = v as Record<string, unknown>;
          return (
            (obj.type as string) ||
            (obj.name as string) ||
            (obj.modality as string) ||
            (obj.value as string) ||
            ''
          );
        }
        return '';
      })
      .filter(Boolean)
      .map((s) => String(s).toUpperCase());
  }, []);

  const hasTextModalities = React.useCallback(
    (m: ResourceItem): boolean => {
      try {
        const normIn = extractModalities((m as any).inputModalities);
        const normOut = extractModalities((m as any).outputModalities);
        return normIn.includes('TEXT') && normOut.includes('TEXT');
      } catch {
        return false;
      }
    },
    [extractModalities]
  );

  const visibleModels: ResourceItem[] = React.useMemo(
    () => (models || []).filter(hasTextModalities),
    [models, hasTextModalities]
  );

  // No debug logs in production branch

  // Unique inference types list (only if provided by backend)
  const allInferenceTypes = React.useMemo(() => {
    const set = new Set<string>();
    for (const m of visibleModels)
      for (const t of getModelInferenceTypes(m)) set.add(t);
    const arr = Array.from(set);
    arr.sort((a, b) =>
      a === 'ON_DEMAND' ? -1 : b === 'ON_DEMAND' ? 1 : a.localeCompare(b)
    );
    return arr;
  }, [visibleModels, getModelInferenceTypes]);

  const anyModelHasInferenceTypes = React.useMemo(() => {
    return visibleModels.some((m) => getModelInferenceTypes(m).length > 0);
  }, [visibleModels, getModelInferenceTypes]);

  // No debug logs in production branch

  // Include both ON_DEMAND and INFERENCE_PROFILE models when available
  React.useEffect(() => {
    if (selectedInferenceTypes.length === 0 && allInferenceTypes.length > 0) {
      // Include both ON_DEMAND and INFERENCE_PROFILE if available to show all models
      const supportedTypes: string[] = [];

      if (allInferenceTypes.includes('ON_DEMAND')) {
        supportedTypes.push('ON_DEMAND');
      }
      if (allInferenceTypes.includes('INFERENCE_PROFILE')) {
        supportedTypes.push('INFERENCE_PROFILE');
      }

      if (supportedTypes.length > 0) {
        setSelectedInferenceTypes(supportedTypes);
      } else {
        // Fallback to all available types
        setSelectedInferenceTypes(allInferenceTypes);
      }
    }
  }, [allInferenceTypes, selectedInferenceTypes.length]);

  // No debug logs in production branch

  // Aggregate providers from models
  const providerAggregation = React.useMemo(() => {
    const counts = new Map<string, number>();
    const filteredByType = visibleModels.filter((m) => {
      const types: string[] = getModelInferenceTypes(m);
      if (selectedInferenceTypes.length === 0) return true;
      if (!types || types.length === 0) return false; // exclude unknown types when a filter is selected
      return types.some((t) => selectedInferenceTypes.includes(t));
    });
    for (const m of filteredByType) {
      const providerName = (m.provider || 'Unknown').trim();
      counts.set(providerName, (counts.get(providerName) || 0) + 1);
    }
    const arr = Array.from(counts.entries()).map(([name, count]) => ({
      name,
      count,
      logoSrc: getProviderLogo(name)
    }));
    arr.sort(
      (a, b) => (b.count || 0) - (a.count || 0) || a.name.localeCompare(b.name)
    );
    return arr;
  }, [visibleModels, selectedInferenceTypes, getModelInferenceTypes]);

  const filteredModelsByProvider = React.useMemo(() => {
    if (!selectedProvider) return [] as ResourceItem[];
    return visibleModels.filter(
      (m) => (m.provider || 'Unknown').trim() === selectedProvider
    );
  }, [visibleModels, selectedProvider]);

  const modelsByProvider = React.useMemo(() => {
    const map: Record<string, ResourceItem[]> = {};
    const filteredByType = visibleModels.filter((m) => {
      const types: string[] = getModelInferenceTypes(m);
      if (selectedInferenceTypes.length === 0) return true;
      if (!types || types.length === 0) return false; // exclude unknown types when a filter is selected
      return types.some((t) => selectedInferenceTypes.includes(t));
    });
    for (const m of filteredByType) {
      const p = (m.provider || 'Unknown').trim();
      if (!map[p]) map[p] = [];
      map[p].push(m);
    }
    return map;
  }, [visibleModels, selectedInferenceTypes, getModelInferenceTypes]);

  const handleReset = () => {
    setSelectedProvider('');
    setSelectedModelId('');
    setSelectedPartner('');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f5f5f5',
        padding: '20px'
      }}
    >
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ marginBottom: 32 }}>
          <Button
            icon={<BackIcon />}
            onClick={() => setCurrentView('main')}
            style={{
              borderRadius: 20,
              border: 'none',
              background: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              padding: '6px 20px',
              height: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              transition: 'all 0.3s ease',
              fontSize: 14,
              fontWeight: 500
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Back to Main Menu
          </Button>
        </div>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Space direction="vertical" size={8}>
            <MessageIcon style={{ fontSize: 32, color: '#1890ff' }} />
            <Title level={3} style={{ margin: 0 }}>
              Direct Model Interaction
            </Title>
          </Space>
        </div>

        {!selectedModelId && (
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                margin: '8px 0 16px 8px'
              }}
            >
              <Title
                level={5}
                style={{ margin: 0, color: '#8c8c8c', fontWeight: 500 }}
              >
                Choose a Provider
              </Title>
              <Tooltip
                placement="left"
                title={
                  "We've filtered out models that don't support either on-demand or inference profile inference, or that don't support text input/output. Check your model's specification or use #llm-playground for help."
                }
              >
                <span
                  style={{
                    color: '#1890ff',
                    cursor: 'help',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    marginRight: 8
                  }}
                >
                  Can't find your model?
                </span>
              </Tooltip>
            </div>
            <ProviderGrid
              providers={providerAggregation}
              loading={modelsLoading}
              onSelect={setSelectedProvider}
              modelsByProvider={modelsByProvider}
              filterTypes={selectedInferenceTypes}
              onModelSelect={(_, modelId) => {
                setSelectedProvider(''); // collapse grid
                setSelectedModelId(modelId);
              }}
            />
          </div>
        )}

        {/* No intermediate list; selection happens from provider dropdown */}

        {!!selectedModelId && (
          <div style={{ marginTop: 8 }}>
            <Button onClick={handleReset} style={{ marginBottom: 12 }}>
              Choose another model
            </Button>
            <ResourceSelector
              type="model"
              title={`Model Conversation`}
              items={visibleModels.filter((m) => m.id === selectedModelId)}
              loading={modelsLoading}
              onItemSelect={setSelectedModelId}
              selectedItemId={selectedModelId}
              partners={PARTNER_OPTIONS}
              selectedPartner={selectedPartner}
              onPartnerSelect={setSelectedPartner}
              showDetails={true}
              fullWidthAfterSelection={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectModelInteractionContent;
